import * as React from 'react';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import ActionLink from 'components/buttons/action-link/ActionLink';

import ContentApprovalUnsortedItem from './ContentApprovalUnsortedItem';

import { t } from 'utils/language';
import { prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';
import { CONTENT_APPROVAL_ADD_NEW_LOOK_ID } from 'utils/contentApproval';

import type {
  ClientApprovalBucketItem,
  ClientApprovalBucketShort,
  Data,
  MixedSelectDropdownItem
} from 'types';

type Props = {
  items: Array<ClientApprovalBucketItem>,
  bucketDetails: Array<ClientApprovalBucketShort>,
  onUnsortedItemSelect: (selectedIndex: number) => void,
  onUnsortedBucketSelect: (title: string, value: string) => void,
  onSelectAllUnsorted: () => void,
  onDeselectAllUnsorted: () => void
};

class ContentApprovalUnsortedItems extends React.PureComponent<Props> {
  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { bucketDetails } = this.props;
    const bucketNamesWithAddNew = bucketDetails.concat([
      {
        id: '-',
        title: '---',
        disabled: true
      },
      {
        id: CONTENT_APPROVAL_ADD_NEW_LOOK_ID,
        title: t('content_approval.buckets.unsorted.footer.add_new'),
        disabled: false
      }
    ]);

    return bucketNamesWithAddNew.map(bucket => ({
      identifier: String(bucket.id),
      isDisabled: bucket.disabled ?? false,
      isSelected: false, // since items are added instantly to a look, the select value should always be default (e.g. "Select a look")
      label: bucket.title,
      value: String(bucket.id)
    }));
  };

  handleLookChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { onUnsortedBucketSelect } = this.props;
    const label = selectedItem?.label || updatedItems[0].label || '';
    const value = selectedItem?.identifier || updatedItems[0].identifier;

    onUnsortedBucketSelect(label, value);
  };

  renderPlaceholder(): React.ReactNode {
    return (
      <div className="content-approval-unsorted-items__placeholder">
        {t('content_approval.buckets.unsorted.placeholder')}
      </div>
    );
  }

  renderItems(): React.ReactNode {
    const { items, onUnsortedItemSelect } = this.props;
    const galleryItems = prepareDeliverableAttachmentsForGallery(items);

    return (
      <div className="content-approval-unsorted-items__items">
        {
          items.map((item, index) => (
            <ContentApprovalUnsortedItem
              key={`content-approval-unsorted-item-${item.id}`}
              item={item}
              galleryItems={galleryItems}
              galleryItemIndex={index}
              onUnsortedItemSelect={onUnsortedItemSelect}
            />
          ))
        }
      </div>
    );
  }

  renderFooter(): React.ReactNode {
    const { items } = this.props;
    const dropdownItems = this.getDropdownItems();
    const isOneItemSelected = items.some(item => item.isSelected);

    return (
      <div className="content-approval-unsorted-items__footer">
        <div className="content-approval-unsorted-items__footer-label">
          {t('content_approval.buckets.unsorted.footer.label')}
        </div>
        <div className="content-approval-unsorted-items__footer-dropdown">
          <MixedSelectDropdown
            name="content-approval-unsorted-items__dropdown"
            items={dropdownItems}
            isDisabled={!isOneItemSelected}
            withSelectedInList
            className="content-approval-unsorted-items__dropdown"
            onItemsToggle={this.handleLookChange}
          />
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { items, onSelectAllUnsorted, onDeselectAllUnsorted } = this.props;

    return (
      <div className="content-approval-unsorted-items">
        <div className="content-approval-unsorted-items__header">
          <span className="content-approval-unsorted-items__title">
            {t('content_approval.buckets.unsorted.title')}
          </span>&nbsp;
          ({t('content_approval.buckets.unsorted.subtitle')})
          <span className="content-approval-unsorted-items__actions">
            <ActionLink
              color="white"
              size="small"
              decoration="none"
              onClick={onSelectAllUnsorted}
            >
              {t('action_button_select_all')}
            </ActionLink>
            <span className="content-approval-unsorted-items__actions-separator">|</span>
            <ActionLink
              color="white"
              size="small"
              decoration="none"
              onClick={onDeselectAllUnsorted}
            >
              {t('action_button_deselect_all')}
            </ActionLink>
          </span>
        </div>
        {
          items.length ?
            this.renderItems() :
            this.renderPlaceholder()
        }
        {this.renderFooter()}
      </div>
    );
  }
}

export default ContentApprovalUnsortedItems;
